<script setup lang="ts">
import { SancareModal, SancareOcticon, SancarePasswordInput } from '@sancare/typescript-commons'
import { defineKeys } from '@sancare/typescript-commons/src/misc/type-utils'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { roleLabels } from '@/misc/constants'

/**
 * Interface to reflect edited data with partial User field
 */
interface UserEditFormData {
  firstName: string
  lastName: string
  email: string
  username: string
  password: string
  passwordConfirmation: string
  role: string
}

/**
 * data passed to the component
 */
interface EditedUser {
  id: number
  firstName?: string
  lastName?: string
  email: string
  username: string
  role: string
  createdFromLdap?: boolean
  sancareUser?: boolean
}

/**
 * Data returned by the component with optional keys
 * Only updated keys are sent
 */
interface SubmittedUserData {
  id?: number
  firstName?: string
  lastName?: string
  email?: string
  username?: string
  role?: string
  password?: string
}

export interface UserEditFormErrors {
  passwordTooShort: boolean
  passwordChars: boolean
  passwordMismatch: boolean
  missingUsername: boolean
  invalidEmail: boolean
  missingEmail?: boolean
}

export interface UserEditModalEmits {
  (event: 'submit-form', data: SubmittedUserData): void
  (event: 'close-modal'): void
}

interface UserEditModalProps {
  showEditor: boolean
  editedUser?: EditedUser | null
}

const props = withDefaults(defineProps<UserEditModalProps>(), {
  editedUser: null
})

const emit = defineEmits<UserEditModalEmits>()

const store = useStore()
const { t } = useI18n()

const getDefaultFormData = (): UserEditFormData => ({
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  passwordConfirmation: '',
  role: 'ROLE_TIM',
})

const getDefaultFormErrors = (): UserEditFormErrors => ({
  passwordTooShort: false,
  passwordChars: false,
  passwordMismatch: false,
  missingUsername: false,
  invalidEmail: false,
})

const editPassword = ref(false)
const requestError = ref<string | null>(null)

const formData = reactive<UserEditFormData>(getDefaultFormData())
const formErrors = reactive<UserEditFormErrors>(getDefaultFormErrors())

const resetFormData = () => Object.assign(formData, getDefaultFormData())
const resetFormErrors = () => Object.assign(formErrors, getDefaultFormErrors())

const availableRoles = computed(() => store.state.login.currentUser.editableRoles)
const updateUserRequest = computed(() => store.state.admin.updateUserRequest)
const createUserRequest = computed(() => store.state.admin.createUserRequest)

const checkPassword = () => {
  formErrors.passwordTooShort = formData.password.length < 8
  formErrors.passwordChars = !formData.password.match(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/)
  if (formData.passwordConfirmation) {
    formErrors.passwordMismatch = formData.password !== formData.passwordConfirmation
  }
}

const checkPasswordConfirmation = () => {
  formErrors.passwordMismatch = formData.password !== formData.passwordConfirmation
}

const checkUsername = () => {
  formErrors.missingUsername = formData.username.length === 0
}

const checkEmail = () => {
  formErrors.missingEmail = formData.email.length === 0
  formErrors.invalidEmail = !formData.email.match(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/)
}

const handleClose = () => {
  resetFormData()
  resetFormErrors()
  editPassword.value = false
  emit('close-modal')
}

const handleSubmit = () => {
  if (!props.editedUser || editPassword.value) {
    checkPassword()
    checkPasswordConfirmation()
  }
  if (!props.editedUser) {
    checkUsername()
    checkEmail()
  }

  if (Object.values(formErrors).some((error) => error)) {
    return
  }

  const submittedData: SubmittedUserData = {}
  if (props.editedUser) {
    submittedData.id = props.editedUser.id
    const fieldsToCheck = defineKeys<UserEditFormData>()(['firstName', 'lastName', 'email', 'username', 'role'])

    fieldsToCheck.forEach((prop) => {
      if (formData[prop] !== props.editedUser![prop]) {
        submittedData[prop] = formData[prop]
      }
    })
    if (editPassword.value) {
      submittedData.password = formData.password
    }
  } else {
    const fieldsToInclude = defineKeys<UserEditFormData>()(['firstName', 'lastName', 'email', 'username', 'role'])

    fieldsToInclude.forEach((prop) => {
      submittedData[prop] = formData[prop]
    })
    submittedData.password = formData.password
  }

  emit('submit-form', submittedData)
}

watch(() => formData.password, () => {
  if (formErrors.passwordTooShort || formErrors.passwordChars) {
    checkPassword()
  }
  if (formErrors.passwordMismatch) {
    checkPasswordConfirmation()
  }
})

watch(() => formData.username, () => {
  if (formErrors.missingUsername) {
    checkUsername()
  }
})

watch(() => formData.passwordConfirmation, () => {
  if (formErrors.passwordMismatch) {
    checkPasswordConfirmation()
  }
})

watch(() => formData.email, () => {
  if (formErrors.invalidEmail) {
    checkEmail()
  }
})

watch(updateUserRequest, (req) => {
  if (req.fetching) {
    requestError.value = null
  } else if (req.error) {
    requestError.value = req.error
  }
})

watch(createUserRequest, (req) => {
  if (req.fetching) {
    requestError.value = null
  } else if (req.error) {
    requestError.value = req.error
  }
})

watch(() => props.editedUser, (user) => {
  if (user) {
    Object.assign(formData, getDefaultFormData(), user, { password: '' })
  }
})
</script>

<template>
  <sancare-modal
    :open="showEditor"
    @close-modal="handleClose"
  >
    <template #header>
      <div
        class="row"
      >
        <div class="sancare-modal-title col">
          {{ editedUser ? "Modifier un utilisateur" : t('user.newUser') }}
        </div>
        <div
          class="sancare-modal-close col-auto"
          @click="handleClose"
        >
          <sancare-octicon
            name="x"
            :width="20"
            :height="20"
          />
        </div>
      </div>
    </template>
    <form
      class="row"
      @submit.prevent="handleSubmit"
    >
      <h3 class="col-12 title-light">
        Informations générales
      </h3>
      <div class="mb-3 col-12 col-md-6">
        <label
          class="small-label"
          for="firstName"
        >
          {{ t('user.firstName') }}
        </label>
        <input
          id="firstName"
          v-model="formData.firstName"
          class="form-control"
        >
      </div>
      <div class="mb-3 col-12 col-md-6">
        <label
          class="small-label"
          for="lastName"
        >
          {{ t('user.lastName') }}
        </label>
        <input
          id="lastName"
          v-model="formData.lastName"
          class="form-control"
        >
      </div>
      <div class="mb-3 required col-12 col-md-6">
        <label
          class="small-label control-label"
          for="username"
        >
          {{ t('user.username') }}
        </label>
        <input
          id="username"
          v-model="formData.username"
          :class="{ 'is-invalid': formErrors.missingUsername }"
          :disabled="editedUser !== null"
          class="form-control"
          @blur="checkUsername"
        >
        <div class="invalid-feedback">
          <div v-if="formErrors.missingUsername">
            L'identifiant est requis.
          </div>
        </div>
      </div>
      <div class="mb-3 required col-12 col-md-6">
        <label
          class="small-label control-label"
          for="email"
        >
          {{ t('user.email') }}
        </label>
        <input
          id="email"
          v-model="formData.email"
          :class="{ 'is-invalid': formErrors.invalidEmail }"
          :disabled="editedUser !== null"
          class="form-control"
          @blur="checkEmail"
        >
        <div class="invalid-feedback">
          <div v-if="formErrors.missingEmail">
            L'adresse email est requise.
          </div>
          <div v-else-if="formErrors.invalidEmail">
            Cette adresse email n'est pas valide.
          </div>
        </div>
      </div>
      <div
        v-if="!editedUser || !editedUser.createdFromLdap"
        class="mb-3 col-12"
      >
        <h3 class="title-light">
          {{ t('user.password') }}
        </h3>
        <div v-if="editedUser && !editPassword">
          <div>
            <button
              class="btn btn-link"
              type="button"
              @click="editPassword = true"
            >
              Modifier le mot de passe
            </button>
          </div>
        </div>
        <div
          v-if="!editedUser || editPassword"
          class="row"
        >
          <div class="mb-3 required col-12 col-md-6">
            <div class="">
              <label
                class="small-label control-label"
                for="password"
              >
                {{ t('user.password') }}
              </label>
              <sancare-password-input
                id="password"
                v-model="formData.password"
                :class="{ 'is-invalid': formErrors.passwordTooShort || formErrors.passwordChars }"
                @blur="checkPassword"
              />
              <div class="invalid-feedback">
                <div v-if="formErrors.passwordTooShort">
                  Ce mot de passe est trop court.
                </div>
                <div v-else-if="formErrors.passwordChars">
                  Le mot de passe doit contenir au moins une minuscule, une majuscule et un chiffre.
                </div>
              </div>
            </div>
          </div>
          <div class="mb-3 required col-12 col-md-6">
            <label
              class="small-label control-label"
              for="passwordConfirmation"
            >
              Confirmation du mot de passe
            </label>
            <sancare-password-input
              id="passwordConfirmation"
              v-model="formData.passwordConfirmation"
              :class="{ 'is-invalid': formErrors.passwordMismatch }"
              @blur="checkPasswordConfirmation"
            />
            <div class="invalid-feedback">
              <div v-if="formErrors.passwordMismatch">
                Les mots de passe sont différents.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 row">
        <h3 class="col-12 title-light">
          Permissions
        </h3>
        <div class="mb-3 col-12 col-md-6">
          <label
            class="small-label"
            for="role"
          >
            Type de compte
          </label>
          <select
            id="role"
            v-model="formData.role"
            :disabled="editedUser && editedUser.sancareUser"
            class="form-select"
          >
            <option
              v-for="role in availableRoles"
              :key="role"
              :value="role"
            >
              {{ roleLabels[role] }}
            </option>
          </select>
        </div>
      </div>
      <!-- TODO: Define and manage teams
      <div class="mb-3 col-12 col-md-6">
        <label
          class="small-label"
          for="role"
        >
          Equipe
        </label>
        <span v-if="teamInput === 'select'">
          <select
            id="team"
            v-model="formData.teamId"
            class="form-select"
          >
            <option
              :value="-1"
              @click="teamInput = 'free'"
            >
              nouvelle equipe
            </option>
            <option
              v-for="team in teamList"
              :value="team.id"
              :key="'team_'+team.id"
            >
              {{ team.name }}
            </option>
          </select>
        </span>
        <span
          v-if="teamInput === 'free'"
          class="row mx-1"
        >
          <input
            v-model="formData.teamName"
            class="col form-control"
          >
          <span
            class="col-auto"
            @click="teamInput = 'select'"
          >
            X
          </span>
        </span>
      </div>
      -->
      <div class="col-12 row align-items-center">
        <div class="col">
          <div
            v-if="requestError"
            class="text-danger"
          >
            {{ requestError }}
          </div>
        </div>
        <div class="col-auto">
          <button class="btn btn-push btn-primary btn-padded">
            {{ t('validate') }}
          </button>
        </div>
      </div>
    </form>
  </sancare-modal>
</template>
